import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Autocomplete,
  Modal,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import { createFilterOptions } from "@mui/material/Autocomplete";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import ReportNavigation from "../../Components/ReportNavigation/ReportNavigation";
import Task from "@mui/icons-material/Task";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { toast } from "react-toastify";

const PlacingList = () => {
  const userToken = LocalstorageHelper.GetUserToken();
  const [open, setOpen] = useState(false);

  // inward list
  const [param, setParam] = useState([]);

  // mode
  const [mode, setMode] = useState("inward");

  const OPTIONS_LIMIT = 15;
  const filterOptions = createFilterOptions({
    limit: OPTIONS_LIMIT,
    trim: true,
  });

  // selections / inputs
  const [searchInput, setSearchInput] = useState(null); // inward selected row
  const [invnoText, setInvnoText] = useState(""); // typed invno (partywise)

  // report preview
  const [pdfStream, setPdfStream] = useState(null);

  // flags
  const [casefileflag, setcaseFileFlag] = useState(false);
  const [inwardText, setInwardText] = useState("");

  // loaders
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingParty, setLoadingParty] = useState(false);

  // purchase party info
  const [partyInfo, setPartyInfo] = useState(null);

  // -----------------------------
  // helpers
  // -----------------------------
  const safeText = (v) => String(v ?? "").trim();

  const currentBillNo = useMemo(() => {
    // When inward: from selected row billno; when partywise: typed invno
    if (mode === "inward") return safeText(searchInput?.billno);
    return safeText(invnoText);
  }, [mode, searchInput, invnoText]);

  const copyToClipboard = async (text, label = "Copied") => {
    const val = safeText(text);
    if (!val) return toast.error("Nothing to copy");
    try {
      await navigator.clipboard.writeText(val);
      toast.success(label);
    } catch (e) {
      // fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = val;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(label);
      } catch (err) {
        toast.error("Copy failed");
      }
    }
  };

  // -----------------------------
  // Fetch inward list (should include billno)
  // -----------------------------
  async function getParam() {
    try {
      const response = await fetch("/reports/purbilldetails", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setParam(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      setParam([]);
    }
  }

  useEffect(() => {
    getParam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // Fetch purchase party by billno (invno param)
  // -----------------------------
  async function fetchPurchaseParty(billno) {
    const invno = safeText(billno);
    if (!invno) return;

    setLoadingParty(true);
    try {
      const res = await fetch(
        `/reports/purchaseparty?invno=${encodeURIComponent(invno)}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        },
      );

      const data = await res.json();
      setPartyInfo(Array.isArray(data) && data.length ? data[0] : null);
    } catch (err) {
      console.log(err.message);
      // keep previous partyInfo? user asked do not remove unless reset
      // so don't setPartyInfo(null) here
    } finally {
      setLoadingParty(false);
    }
  }

  // -----------------------------
  // modal close
  // -----------------------------
  const handleClose = () => {
    if (loadingReport) return;
    setOpen(false);
  };

  // -----------------------------
  // mode change (IMPORTANT: do NOT reset data)
  // -----------------------------
  const handleModeChange = (e, newMode) => {
    if (!newMode) return;
    if (loadingReport) return;
    setMode(newMode);
    // ✅ do NOT clear pdfStream / partyInfo / inputs here
  };

  // -----------------------------
  // Reset button (only way to clear)
  // -----------------------------
  const handleReset = () => {
    if (loadingReport) return;

    setSearchInput(null);
    setInvnoText("");
    setPdfStream(null);
    setPartyInfo(null);
    setLoadingParty(false);
    setcaseFileFlag(false);
  };

  const showDisabled =
    loadingReport ||
    (mode === "inward"
      ? !searchInput?.inwardid
      : safeText(invnoText).length === 0);

  // -----------------------------
  // Show Report
  // -----------------------------
  const handleReport = async (e) => {
    e.preventDefault();
    if (showDisabled) return;

    setLoadingReport(true);
    try {
      // INWARD mode
      if (mode === "inward") {
        const inwardid = searchInput?.inwardid;
        if (!inwardid) return;

        const pdfBlob = await fetch(
          `/reports/getplacinglist/${inwardid}/${casefileflag}`,
          {
            method: "GET",
            headers: { sptoken: userToken },
          },
        ).then((r) => r.blob());

        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfStream(pdfUrl);
        return;
      }

      // PARTYWISE mode
      const invno = safeText(invnoText);
      if (!invno) return;

      const pdfBlob = await fetch(`/reports/getplacinglist/partywise`, {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invno }),
      }).then((r) => r.blob());

      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfStream(pdfUrl);
    } catch (err) {
      console.log(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <div>
      <Button
        onClick={() => setOpen(true)}
        variant="contained"
        color="secondary"
        size="large"
        sx={{ boxShadow: "2px 2px 6px  #404142" }}
      >
        Placing List
      </Button>

      <Modal open={open} aria-labelledby="modl-box" aria-describedby="placing">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 6,
            mt: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: "20px",
              rowGap: "10px",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={casefileflag}
                  onChange={(e) => setcaseFileFlag(e.target.checked)}
                />
              }
              label="CaseWisePlacingList"
            />

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              size="small"
              disabled={loadingReport}
            >
              <ToggleButton value="inward">Inward</ToggleButton>
              <ToggleButton value="partywise">Partywise</ToggleButton>
            </ToggleButtonGroup>

            {/* Input based on mode */}
            {mode === "inward" ? (
              <Autocomplete
                id="inward"
                value={searchInput}
                inputValue={inwardText}
                onInputChange={(event, newInputValue) => {
                  setInwardText(newInputValue); // ✅ allows typing numbers
                }}
                filterOptions={filterOptions}
                onChange={(event, newValue) => {
                  setSearchInput(newValue);

                  // keep textbox showing the selected inwardno
                  setInwardText(newValue?.inwardno || "");

                  // fetch party using billno (no reset)
                  if (newValue?.billno) {
                    fetchPurchaseParty(newValue.billno);
                  }
                }}
                options={param}
                getOptionLabel={(option) =>
                  option?.inwardno ? String(option.inwardno) : ""
                } // ✅ ensure string
                isOptionEqualToValue={(opt, val) =>
                  opt?.inwardid === val?.inwardid
                } // ✅ stable match
                sx={{ width: "30%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Inward No"
                    size="small"
                    disabled={loadingReport}
                  />
                )}
                size="small"
                disabled={loadingReport}
              />
            ) : (
              <TextField
                size="small"
                value={invnoText}
                onChange={(e) => setInvnoText(e.target.value)}
                placeholder="Invoice No (type here)"
                sx={{ width: "30%" }}
                required
                disabled={loadingReport}
              />
            )}

            {/* Show Report */}
            <Button
              type="submit"
              onClick={handleReport}
              variant="contained"
              component="label"
              size="large"
              sx={{ boxShadow: "2px 2px 6px  #404142", minWidth: 160 }}
              endIcon={loadingReport ? null : <Task />}
              disabled={showDisabled}
            >
              {loadingReport ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Show Report"
              )}
            </Button>

            {/* Reset button (ONLY way to clear) */}
            <Button
              variant="outlined"
              size="large"
              onClick={handleReset}
              disabled={loadingReport}
              startIcon={<RestartAltIcon />}
              sx={{ minWidth: 130 }}
            >
              Reset
            </Button>

            {/* Close */}
            <Button
              variant="contained"
              size="medium"
              onClick={handleClose}
              color="error"
              disabled={loadingReport}
            >
              Close
            </Button>
          </Box>

          {/* ✅ Copy area (no IDs shown) */}
          <Box
            sx={{
              mt: 2,
              p: 1,
              border: "1px solid #ddd",
              borderRadius: 1,
              backgroundColor: "#fafafa",
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2">
                <b>{mode === "inward" ? "Bill No" : "Invoice No"}:</b>{" "}
                {currentBillNo || "—"}
              </Typography>

              <Tooltip
                title={`Copy ${mode === "inward" ? "Bill No" : "Invoice No"}`}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      copyToClipboard(
                        currentBillNo,
                        `${mode === "inward" ? "Bill No" : "Invoice No"} copied`,
                      )
                    }
                    disabled={!currentBillNo}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {loadingParty && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Loading party…</Typography>
                </Box>
              )}

              {!loadingParty && partyInfo?.party && (
                <Typography variant="body2">
                  <b>Party:</b> {partyInfo.party}
                </Typography>
              )}
            </Stack>
          </Box>

          {pdfStream && <ReportNavigation />}

          <Box sx={{ backgroundColor: "white" }}>
            {pdfStream && (
              <iframe
                src={pdfStream}
                width="100%"
                height="350px"
                title="report"
              />
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default PlacingList;
